import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  TertiaryButton,
  Text,
  Tooltip,
  chakra,
} from '@enechain/ecloud-designsystem'
import {
  FaAnglesLeftRegular,
  FaAnglesRightRegular,
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { now as serverNow } from '~/models/clock'

/**
 * 日付セルを自前で描く DAY グリッド (DS `Calendar` の置き換え)。
 *
 * ## なぜ DS `Calendar` を使わないか
 *
 * spec `shared-components.md` §8 (AC-SC-027 / AC-SC-028) は、カレンダービュー上で
 * 祝日・特異日にマーカーを出し、ホバーで名称を読ませることを P0 で求めている。
 * **DS にはこれを実現する口が無い** — `CalendarProps` は
 * `value / maxDate / minDate / includeDates / excludeDates / calendarType / onChange`
 * だけで、日付ごとの装飾フック (`renderDay` / `dayProps` / `modifiers` 相当) を
 * 持たない。DS 要望 enechain/eclair-designsystem#1222 は 2026-08-06 から無反応で、
 * **最新の 0.17.10 でも `CalendarProps` は 0.14.2 と同一**である (2026-09-15 に
 * タグ指定で実物を確認。リポジトリ全体のコード検索でも該当 prop は 0 件)。
 *
 * 外から DOM を装飾する案も採らない。DS の日セルは
 * `<button type="button" class="chakra-button css-it5wyu">15</button>` で、
 * **日付を示す属性が一切無い**。テキスト一致 + 位置からの月推測 + emotion の
 * ハッシュ class に依存することになり、DS 更新で黙って壊れる。
 *
 * ## 何を保つか
 *
 * 本 grid は `CompactCivilDateField` の**既存の契約**をそのまま満たす:
 *
 * - 日セルは `role="button"` で、**accessible name は日番号のみ** (`'20'`)
 * - `maxDate` は **境界 inclusive**。超過日は押しても `onChange` を呼ばない
 * - 日付を選ぶと `onChange(date)` を呼ぶ (popup を閉じるのは呼び出し元の責務)
 *
 * `minDate` は `maxDate` と対称の境界 (inclusive) として実装する
 * (`bidding/intraday-bidding.md` AC-BIN-000a: 入札追加タブの受渡日は過去日選択
 * 不可)。`includeDates` / `excludeDates` は引き続き**アプリ内に利用箇所が無い**
 * ため実装しない (必要になった時点で足す。使われない分岐を先に作らない)。
 *
 * 見出しは DS `Calendar` の Navigation と同じ並び (« 前年 ‹ 前月 年月 › 翌月 » 翌年、
 * アイコンも DS と同じ Angles / Chevron)。DS を置き換えた時に年送りだけ落ちており、
 * 離れた年の日付 (マスタの適用開始日など) を入れるのに月送りを何十回も押していた
 * (#9948)。8080 / React mock の `DateInput` は DS どおり年送りを持つ。
 */

/**
 * 日曜起点の曜日。表示文言は `common:date.calendar.weekday.*` (日 / Sun …)。
 * 直書きの日本語だと英語表示でも曜日だけ日本語のまま残っていた (#9948)。
 * `Date#getDay()` の値で引ける (閉じた入力欄の曜日表示も同じ文言を使う)。
 */
export const WEEKDAY_KEYS = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
] as const

/** 同一日か (時刻は見ない)。 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** その日の 00:00 に丸めた Date。日付比較の基準を揃える。 */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * 表示月のセル配列 (前月末・翌月頭のパディング込み、常に 6 週 = 42 セル)。
 *
 * 週数を月によって変えると月送りのたびに popup の高さが動く。DS `Calendar` も
 * 6 週固定なので合わせる。
 */
function monthCells(viewYear: number, viewMonth: number): Date[] {
  const first = new Date(viewYear, viewMonth, 1)
  const start = new Date(viewYear, viewMonth, 1 - first.getDay())
  return Array.from(
    { length: 42 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  )
}

/**
 * 表示月 (`month` は 1 始まり) の grid が並べる 42 日の先頭日と末尾日。
 * 表示中の日だけを問い合わせる呼び出し元 (`onViewMonthChange`) が範囲を組むのに使う。
 */
export function dayGridRange(
  year: number,
  month: number,
): { from: Date; to: Date } {
  const cells = monthCells(year, month - 1)
  return { from: cells[0], to: cells[cells.length - 1] }
}

/** 日ごとの選択可否と、その日の hover / focus に出す説明。 */
export type DayCellState = {
  /** true の日は押しても `onChange` を呼ばない。 */
  disabled: boolean
  /** その日の tooltip に出す文言 (選べない理由など)。 */
  hint?: string
}

/** 日付 → その日の特異日 (複数可)。キーは `YYYY-MM-DD`。 */
export type SingularDayLabel = {
  name: string
  /** 表示済みのカテゴリ名 (i18n 解決済み)。 */
  categoryLabel: string
}

type Props = {
  /** 選択中の日付。 */
  value?: Date
  /** 日付を選んだ時に呼ぶ。 */
  onChange: (d: Date) => void
  /** これより後の日付を選択不可にする (境界は inclusive)。 */
  maxDate?: Date
  /** これより前の日付を選択不可にする (境界は inclusive。`maxDate` と対称)。 */
  minDate?: Date
  /**
   * 日付キー (`YYYY-MM-DD`) → その日の特異日。
   *
   * **opt-in**。渡さなければ装飾しない (全画面に一斉に出さず、エリア文脈を
   * 持つ呼び出し元から明示的に渡す)。同じ日に複数の特異日が載りうる
   * (全エリア共通 + エリア限定の併存) ため配列で受ける。
   */
  singularDaysByDateKey?: ReadonlyMap<string, SingularDayLabel[]>
  /**
   * 日ごとの選択可否と説明。**opt-in**。`undefined` を返した日と未指定時は
   * 従来どおり (`minDate` / `maxDate` だけで決まる)。
   */
  dayState?: (d: Date) => DayCellState | undefined
  /**
   * 表示月が決まった時 (初回描画 = popup を開いた時と、月・年送りのたび) に呼ぶ。
   * `month` は 1 始まり。
   */
  onViewMonthChange?: (year: number, month: number) => void
}

/** `YYYY-MM-DD` (ローカル暦日)。`~/models/civilDate` の dateKeyOf と同形式。 */
function dateKey(d: Date): string {
  const y = d.getFullYear().toString().padStart(4, '0')
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * 見出しの送りボタン (« ‹ › »)。DS `Calendar` の ArrowButton と同じ
 * `TertiaryButton` 32px・icon 20px。
 */
const NavButton = ({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactElement
}): React.ReactElement => (
  <Tooltip content={label}>
    <TertiaryButton
      width="32px"
      padding={0}
      aria-label={label}
      onClick={onClick}
      size="sm"
    >
      {children}
    </TertiaryButton>
  </Tooltip>
)

/** DS `Calendar` の曜日見出しの色 (`constants.ts` の weekList)。 */
const WEEKDAY_HEADER_COLORS = [
  'Text.negativeMid',
  'Text.midEmphasis',
  'Text.midEmphasis',
  'Text.midEmphasis',
  'Text.midEmphasis',
  'Text.midEmphasis',
  'Text.infoMid',
] as const

export const DayGrid = ({
  value,
  onChange,
  maxDate,
  minDate,
  singularDaysByDateKey,
  dayState,
  onViewMonthChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // ブラウザ時計ではなくサーバー時刻補正済みの現在時刻を使う
  // (`~/models/clock`)。端末の時計がずれていると「今日」 がずれ、値未設定時に
  // 開く月が変わる。
  const today = startOfDay(serverNow())
  const base = value ?? today
  const [view, setView] = useState({
    year: base.getFullYear(),
    month: base.getMonth(),
  })

  // 呼び出し元が毎描画で新しい関数を渡しても、表示月が変わった時だけ通知する
  const onViewMonthChangeRef = useRef(onViewMonthChange)
  onViewMonthChangeRef.current = onViewMonthChange
  useEffect(() => {
    onViewMonthChangeRef.current?.(view.year, view.month + 1)
  }, [view.year, view.month])

  const cells = useMemo(
    () => monthCells(view.year, view.month),
    [view.year, view.month],
  )
  const maxDay = maxDate !== undefined ? startOfDay(maxDate) : undefined
  const minDay = minDate !== undefined ? startOfDay(minDate) : undefined

  return (
    // 幅は DS `Calendar` の日の格子と同じ 7 × 32px + 6 × 16px = 320px
    <Box flexDirection="column" gap={4} display="flex" width="320px">
      <Box
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        display="flex"
      >
        <Box justifyContent="flex-start" gap={2} display="flex" width="72px">
          <NavButton
            label={t('common:date.calendar.prevYear')}
            onClick={() => {
              setView((v) => ({ ...v, year: v.year - 1 }))
            }}
          >
            <FaAnglesLeftRegular boxSize={iconSize.labeledButton} />
          </NavButton>
          <NavButton
            label={t('common:date.calendar.prevMonth')}
            onClick={() => {
              setView((v) =>
                v.month === 0
                  ? { year: v.year - 1, month: 11 }
                  : { ...v, month: v.month - 1 },
              )
            }}
          >
            <FaChevronLeftRegular boxSize={iconSize.labeledButton} />
          </NavButton>
        </Box>
        <Text
          textStyle="headerMd"
          flexGrow={1}
          color="Text.highEmphasis"
          textAlign="center"
        >
          {t('common:date.calendar.yearMonth', {
            year: view.year,
            month: view.month + 1,
          })}
        </Text>
        <Box justifyContent="flex-end" gap={2} display="flex" width="72px">
          <NavButton
            label={t('common:date.calendar.nextMonth')}
            onClick={() => {
              setView((v) =>
                v.month === 11
                  ? { year: v.year + 1, month: 0 }
                  : { ...v, month: v.month + 1 },
              )
            }}
          >
            <FaChevronRightRegular boxSize={iconSize.labeledButton} />
          </NavButton>
          <NavButton
            label={t('common:date.calendar.nextYear')}
            onClick={() => {
              setView((v) => ({ ...v, year: v.year + 1 }))
            }}
          >
            <FaAnglesRightRegular boxSize={iconSize.labeledButton} />
          </NavButton>
        </Box>
      </Box>

      <Box gridGap={4} gridTemplateColumns="repeat(7, 1fr)" display="grid">
        {WEEKDAY_KEYS.map((w, i) => (
          <Text key={w} color={WEEKDAY_HEADER_COLORS[i]} textAlign="center">
            {t(`common:date.calendar.weekday.${w}`)}
          </Text>
        ))}

        {cells.map((cell) => {
          const inMonth = cell.getMonth() === view.month
          const overMax =
            maxDay !== undefined && cell.getTime() > maxDay.getTime()
          const underMin =
            minDay !== undefined && cell.getTime() < minDay.getTime()
          const cellState = dayState?.(cell)
          const unavailable = cellState?.disabled === true
          const outOfRange = overMax || underMin || unavailable
          const hint = cellState?.hint
          const selected = value !== undefined && isSameDay(cell, value)
          const singulars = singularDaysByDateKey?.get(dateKey(cell)) ?? []
          const isSingular = singulars.length > 0
          const isToday = isSameDay(cell, today)
          const isSunday = cell.getDay() === 0
          const isSaturday = cell.getDay() === 6

          // 見た目は DS `DatePicker` の日セル (32px の丸・今日は輪・選択日は塗り・月外は灰)
          // に、 eBalance の暦の色だけを足す (React mock `DateInput` / 8080 と同じ規則、
          // `design/ui-feasibility/components/datepicker.md`「目標の見た目」)。
          // - 選択日・選択不可日は DS の表現のまま。 暦の色を付けない
          // - 特異日: warning の文字・太字・地・内枠。 日曜より優先
          // - 日曜は赤、 土曜は青。 **月外の日にも同じ色を付ける** (DS の月外の灰より優先)
          // - 今日は DS の輪 (accent の枠)。 暦の色がある日は文字色だけ暦の色にする
          // 祝日は、祝日データの期間取得が未配線のため扱わない (#13081)。
          const decorated = !selected && !outOfRange
          const color = selected
            ? 'Text.highEmphasisInverse'
            : outOfRange
              ? 'Text.disabled'
              : isSingular
                ? 'Text.warningHigh'
                : isSunday
                  ? 'Text.negativeHigh'
                  : isSaturday
                    ? 'Text.infoHigh'
                    : isToday
                      ? 'Text.accentPrimary'
                      : inMonth
                        ? 'Text.highEmphasis'
                        : 'Text.lowEmphasis'

          const dayButton = (
            <chakra.button
              key={dateKey(cell)}
              textStyle="textLg"
              alignItems="center"
              justifyContent="center"
              display="flex"
              width="32px"
              minWidth="32px"
              height="32px"
              // `Text.highEmphasisInverse` は塗りの上の反転文字 (#ffffff)。 旧 `Text.onFill` は
              // DS に存在しないトークンで、 選択日の数字が読めなくなっていた (2026-09-16)。
              color={color}
              fontWeight={decorated && isSingular ? 'bold' : 'semibold'}
              borderWidth={!selected && isToday ? '1px' : undefined}
              borderStyle={!selected && isToday ? 'solid' : undefined}
              borderColor={
                !selected && isToday ? 'Border.accentPrimary' : undefined
              }
              borderRadius="full"
              // 特異日の内枠は `Text.warningMid` (Border 系に warning の並びが無い)。
              boxShadow={
                decorated && isSingular
                  ? 'inset 0 0 0 1px {colors.Text.warningMid}'
                  : undefined
              }
              _hover={
                decorated
                  ? {
                      backgroundColor: 'Hover.accentPrimaryLow',
                      color: 'Text.highEmphasis',
                    }
                  : undefined
              }
              _focusVisible={{
                outline: 'none',
                boxShadow: '0 0 0 3px {colors.Focus.clarity}',
              }}
              cursor={outOfRange ? 'not-allowed' : 'pointer'}
              aria-disabled={unavailable || undefined}
              backgroundColor={
                selected
                  ? 'Surface.accentPrimary'
                  : decorated && isSingular
                    ? 'Surface.warningLow'
                    : 'transparent'
              }
              data-ebalance-saturday={isSaturday ? 'true' : undefined}
              data-ebalance-special-day={isSingular ? 'true' : undefined}
              data-ebalance-sunday={isSunday ? 'true' : undefined}
              data-ymd={dateKey(cell)}
              onClick={() => {
                // 選べない日 (maxDate 超過 / minDate 未満 / dayState が disabled) は
                // **押せるが何も起きない** (契約テストが固定)。 native の disabled に
                // すると hover / focus が届かず、選べない理由の tooltip を出せない。
                if (outOfRange) return
                onChange(cell)
              }}
              type="button"
            >
              {cell.getDate()}
            </chakra.button>
          )

          // AC-SC-028: マーカー付き日付のホバーで名称を出す。該当が複数あれば
          // どれを優先するかを勝手に決めず、全部並べる。選べない理由は名称より先に出す。
          const tooltipLines = [
            ...(hint !== undefined && hint !== '' ? [hint] : []),
            ...singulars.map((s) =>
              t('common:date.singularDay.tooltip', {
                name: s.name,
                category: s.categoryLabel,
              }),
            ),
          ]
          if (tooltipLines.length === 0) return dayButton

          return (
            <Tooltip key={dateKey(cell)} content={tooltipLines.join(' / ')}>
              {dayButton}
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}
